import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  useMediaQuery,
  useTheme,
  StepConnector,
  styled,
} from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { generateDocx } from "./generateDocx"; // ggf. Pfad anpassen
import { generatePDF } from "./generatePDF2"; // PDF Export hinzufügen

import StartPage from "./StartPage";
import PreviewMode from "./PreviewMode";
import FreigabeForm from "./steps/FreigabeForm";
import UnternehmenForm from "./steps/UnternehmenForm";
import OrganisationForm from "./steps/OrganisationForm";
import RelevanteUnterlagenForm from "./steps/RelevanteUnterlagenForm";
import EinweisungForm from "./steps/EinweisungForm";
import AbgrenzungForm from "./steps/AbgrenzungForm";
import OrganisationSicherheitForm from "./steps/OrganisationSicherheitForm";
import ZustaendigkeitenForm from "./steps/ZustaendigkeitenForm";
import IKSForm from "./steps/IKSForm";
import VerfahrenMassnahmenForm from "./steps/VerfahrenMassnahmenForm";
import ERechnungenForm from "./steps/ERechnungenForm";
import PreviewForm from "./steps/PreviewForm";
import MitgeltendeUnterlagenForm from "./steps/MitgeltendeUnterlagenForm";
import BelegOrgForm from "./steps/BelegOrginaerPapierform";

const steps = [
  "Unternehmen",
  "Organisation",
  "Relevante Unterlagen",
  "Einweisung",
  "Abgrenzung",
  "Organisation & Sicherheit",
  "Zuständigkeiten",
  "IKS und Datenschutz",
  "Verfahren und Maßnahmen",
  "Belege originär in Papierform",
  "E-Rechnungen",
  "Freigabe",
  "Mitgeltende Unterlagen",
  "Zusammenfassung"
];

const StepComponents = [
  UnternehmenForm,
  OrganisationForm,
  RelevanteUnterlagenForm,
  EinweisungForm,
  AbgrenzungForm,
  OrganisationSicherheitForm,
  ZustaendigkeitenForm,
  IKSForm,
  VerfahrenMassnahmenForm,
  BelegOrgForm,
  ERechnungenForm,
  FreigabeForm,
  MitgeltendeUnterlagenForm,
  PreviewForm,

];

export default function StepperForm({ isFullVersion = false }) {
  const methods = useForm();
  const [activeStep, setActiveStep] = useState(0);
  const [showStartPage, setShowStartPage] = useState(true);
  const [showPreviewMode, setShowPreviewMode] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeStep]);

  // Alert when leaving the page
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Haben Sie Ihre Daten exportiert? Wenn nicht, gehen alle eingegebenen Daten verloren!';
      return 'Haben Sie Ihre Daten exportiert? Wenn nicht, gehen alle eingegebenen Daten verloren!';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleStart = () => {
    setShowStartPage(false);
  };

  const handleUpgrade = () => {
    // Weiterleitung zur Vollversion-Seite
    window.location.href = '/vollversion';
  };

  const handleNext = methods.handleSubmit(() => {
    const nextStep = activeStep + 1;
    
    // Nach Schritt 3 (Index 2) Preview-Modus aktivieren, wenn nicht Vollversion
    if (nextStep === 3 && !isFullVersion) {
      setShowPreviewMode(true);
    } else {
      setActiveStep(nextStep);
    }
  });

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleStepClick = (stepIndex) => {
    setActiveStep(stepIndex);
  };

  const handleFinish = methods.handleSubmit((data) => {
    generatePDF(data);
  });

  const handlePDFExport = methods.handleSubmit((data) => {
    generatePDF(data);
  });

  const handleExport = () => {
    const data = methods.getValues();
    const csvContent = Object.entries(data)
      .map(([key, value]) => `${key},${JSON.stringify(value)}`)
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "verfahrensdokumentation_daten.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split("\n");
      const data = {};
  
      const safeParse = (value) => {
        try {
          if (value === undefined || value.trim() === "" || value.trim() === "undefined") return undefined;
          return JSON.parse(value);
        } catch (e) {
          console.warn(`Konnte Wert "${value}" nicht parsen:`, e);
          return undefined;
        }
      };
  
      lines.forEach((line) => {
        const [key, value] = line.split(/,(.+)/); // trennt nur das erste Komma
        if (key) data[key] = safeParse(value);
      });
  
      methods.reset(data);
    };
    reader.readAsText(file);
  };
  

  const StepComponent = StepComponents[activeStep];

  // Show start page if showStartPage is true
  if (showStartPage) {
    return <StartPage onStart={handleStart} />;
  }

  // Show preview mode after step 3 if not full version
  if (showPreviewMode) {
    const formData = methods.getValues();
    return <PreviewMode formData={formData} onUpgrade={handleUpgrade} />;
  }

  return (
    <FormProvider {...methods}>
      <Box sx={{ width: "100%", overflowX: isMobile ? "auto" : "visible" }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel={!isMobile}
          orientation={isMobile ? "vertical" : "horizontal"}
          sx={{ flexWrap: isMobile ? "nowrap" : "wrap", mb: 4 }}
        >
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                onClick={() => {
                  // Nur erlauben bis Schritt 3 wenn nicht Vollversion
                  if (index <= 2 || isFullVersion) {
                    handleStepClick(index);
                  }
                }}
                sx={{
                  cursor: (index <= 2 || isFullVersion) ? "pointer" : "not-allowed",
                  "& .MuiStepLabel-label": {
                    cursor: (index <= 2 || isFullVersion) ? "pointer" : "not-allowed",
                    opacity: (index > 2 && !isFullVersion) ? 0.5 : 1,
                  },
                  "& .MuiStepLabel-iconContainer": {
                    cursor: (index <= 2 || isFullVersion) ? "pointer" : "not-allowed",
                    opacity: (index > 2 && !isFullVersion) ? 0.5 : 1,
                  },
                }}
              >
                {label}
                {index > 2 && !isFullVersion && (
                  <span style={{ fontSize: '0.7rem', color: '#666', marginLeft: '8px' }}>
                    (Premium)
                  </span>
                )}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Export/Import Buttons */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <Button variant="outlined" onClick={handleExport}>
          Exportieren
        </Button>

        <Button
          variant="outlined"
          onClick={() => document.getElementById("importInput").click()}
        >
          Importieren
        </Button>

 

        <input
          id="importInput"
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={handleImport}
        />
      </Box>

      <Box sx={{ mt: 4 }}>
        {activeStep === steps.length - 1 ? (
          <StepComponent onExport={handleExport} />
        ) : (
          <StepComponent />
        )}
        <Box sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}>
          {activeStep > 0 && (
            <Button variant="outlined" onClick={handleBack}>
              Zurück
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            activeStep === 2 && !isFullVersion ? (
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => setShowPreviewMode(true)}
                sx={{ 
                  background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                  boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
                }}
              >
                Preview anzeigen
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                Weiter
              </Button>
            )
          ) : (
            <Button variant="contained" color="success" onClick={handleFinish}>
              Fertigstellen
            </Button>
          )}
        </Box>
      </Box>

      {/* 🆕 Hier das Logo mittig mit zusätzlichem Abstand 
      <Box sx={{ mt: 8, mb: 8, display: "flex", justifyContent: "center" }}>
        <img
          src="https://images.squarespace-cdn.com/content/v1/61699dd6bb0309148629f8d2/20dd106a-a87c-4db3-afab-7de63562ba10/Bildschirmfoto+2022-02-18+um+14.35.27.png?format=1500w"
          alt="Logo"
          style={{ maxWidth: "300px", width: "100%", height: "auto" }}
        />
      </Box>
*/}

 

    </FormProvider>
  );
}